import React, { Suspense, useEffect, useRef, useState } from "react";

import UnsuitableReasonModalContainer from "@/components/shared/business/UnsuitableReason/components/UnsuitableReasonModalContainer/UnsuitableReasonModalContainer";
import { useHandleUnsuitablePress } from "@/components/shared/business/UnsuitableReason/hooks/useHandleUnsuitablePress";
import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { usePressClassificationRoles } from "@libs/roles/hooks/usePressClassificationRoles";
import { usePressIdFromURL } from "@utils/getStateFromURL";

import CancelPublishArticleModal from "./CancelPublishArticleModal/CancelPublishArticleModal";
import * as S from "./EditArticleUnsuitableButton.style";

const EditArticleUnsuitableButton = () => {
  const draftArticleId = usePressIdFromURL();
  const { data: articleInfo } = useArticleInfoQuery(Number(draftArticleId));
  const unsuitableRef = useRef<HTMLDivElement>(null);

  const { hasUnsuitablePressRoles } = usePressClassificationRoles();

  const duplArticleId = articleInfo?.duplArticleId || null;
  const articleRejectReason = articleInfo?.articleRejectReason || null;
  const isRejectedReason = !!articleRejectReason;
  const draftArticleStatusHighest =
    articleInfo?.draftArticleStatusHighest || "";
  const isReviewRequired = articleInfo?.isReviewRequired === true;

  const targetRejectReason = articleRejectReason
    ? `${articleRejectReason}${duplArticleId ? ` (${duplArticleId})` : ""}`
    : null;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);

  const {
    openModalUnsuitablePress,
    closeModalUnsuitablePress,
    isOpenModalUnsuitablePress,
  } = useHandleUnsuitablePress({ type: "PRESS_DETAIL" });

  const handleUnsuitableButtonActive = async () => {
    if (isOpenModalUnsuitablePress) return closeModalUnsuitablePress();

    openModalUnsuitablePress();
  };

  useEffect(() => {
    const handleCheckClickOutsideUnsuitable = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickReasonButton = unsuitableRef.current?.contains(target);
      const deleteReasonButton = target.dataset.button;

      if (clickReasonButton) return;
      if (deleteReasonButton) return;

      closeModalUnsuitablePress();
    };

    window.addEventListener("click", handleCheckClickOutsideUnsuitable);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideUnsuitable);
  }, [closeModalUnsuitablePress]);

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(false);
    handleUnsuitableButtonActive();
    setIsConfirm(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleButtonClick = () => {
    if (draftArticleStatusHighest !== "S920") {
      handleUnsuitableButtonActive();
      setIsConfirm(true);
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <S.UnsuitableButtonWrapper
      ref={unsuitableRef}
      isEnableUnsuitablePress={isRejectedReason}
      disabled={!hasUnsuitablePressRoles}
    >
      <S.UnsuitableButton
        type="button"
        onClick={handleButtonClick}
        isEnableUnsuitablePress={isRejectedReason}
        disabled={!hasUnsuitablePressRoles}
        isReviewRequired={isReviewRequired}
      >
        <>
          {isRejectedReason
            ? targetRejectReason
            : isReviewRequired
            ? `Overlapped Id: #${articleInfo.reviewId} (Review Required)`
            : "Unsuitable / Review Required"}
        </>
      </S.UnsuitableButton>

      <CancelPublishArticleModal
        closeModal={handleClose}
        isOpen={isModalOpen}
        onConfirm={handleConfirm}
      />

      {isConfirm && (
        <Suspense fallback={<></>}>
          <UnsuitableReasonModalContainer
            closeModalUnsuitablePress={closeModalUnsuitablePress}
            isOpenModalUnsuitablePress={isOpenModalUnsuitablePress}
            articleRejectReason={articleRejectReason}
            unsuitableReasonUsageType="PRESS_DETAIL"
            draftArticleId={draftArticleId}
          />
        </Suspense>
      )}
    </S.UnsuitableButtonWrapper>
  );
};

export default EditArticleUnsuitableButton;
